import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RefreshCw, ShoppingBag, UserPlus, Users } from 'lucide-react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { get } from '@/lib/api/client';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Registration totals across the top of the Users page.
 *
 * `analytics/customers/{mode}/{span}` answers both cards:
 *   4/6  — lifetime totals, with the "at least one order" subset
 *   0/1  — today only
 *
 * The original wired these two backwards: `getCustomers()` requested `0/1` and
 * stored it as `todayCustomersData`, while `getTodayCustomers()` requested
 * `4/${timeSpan}` and stored it as `customersData`. It worked because both ran
 * together and each set the other's state, but `setLoadingCustomers(true)` in
 * the first was only ever cleared by the second — so a failure in one left the
 * other card spinning forever.
 */

interface CustomerAnalytics {
  totalUsers: number;
  totalUsersWithAtleastOneOrder: number;
}

/** Lifetime totals. The `6` is the span the original asked for. */
function useLifetimeStats() {
  return useQuery({
    queryKey: ['customers', 'analytics', 'lifetime'],
    queryFn: () => get<CustomerAnalytics>('analytics/customers/4/6'),
    staleTime: 5 * 60 * 1000,
  });
}

function useTodayStats() {
  return useQuery({
    queryKey: ['customers', 'analytics', 'today'],
    queryFn: () => get<CustomerAnalytics>('analytics/customers/0/1'),
    staleTime: 5 * 60 * 1000,
  });
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  loading,
  busy,
  failed,
}: {
  icon: typeof Users;
  label: string;
  value: number | undefined;
  hint?: string;
  loading: boolean;
  /** A refresh with the previous figure still on screen. */
  busy: boolean;
  failed: boolean;
}) {
  const refreshing = busy && !loading;

  return (
    <div className="bg-card relative rounded-xl border p-4">
      <BusyOverlay show={refreshing} size="sm" />
      <div
        className={cn(
          'flex items-center gap-2',
          refreshing && 'opacity-60 transition-opacity'
        )}
      >
        <span className="bg-primary/10 text-primary flex size-7 shrink-0 items-center justify-center rounded-lg">
          <Icon className="size-3.5" />
        </span>
        <p className="text-muted-foreground truncate text-xs font-medium">
          {label}
        </p>
      </div>

      <div
        className={cn(
          'mt-2.5 flex items-baseline gap-2',
          refreshing && 'opacity-60 transition-opacity'
        )}
      >
        {loading ? (
          <Skeleton className="h-8 w-24" />
        ) : failed ? (
          // A dash, not a zero: "we could not load this" and "there are none"
          // must not look the same on a metric someone acts on.
          <span className="text-muted-foreground text-3xl font-semibold">—</span>
        ) : (
          <span className="text-3xl font-semibold tabular-nums">
            {formatNumber(value)}
          </span>
        )}
        {hint && !loading && !failed && (
          <span className="text-muted-foreground text-sm tabular-nums">
            {hint}
          </span>
        )}
      </div>
    </div>
  );
}

export function CustomerStats({ className }: { className?: string }) {
  const { t } = useTranslation();
  const lifetime = useLifetimeStats();
  const today = useTodayStats();

  const total = lifetime.data?.totalUsers;
  const withOrders = lifetime.data?.totalUsersWithAtleastOneOrder;

  // Guard the divide: a fresh platform has zero users and the original happily
  // rendered "NaN%".
  const share =
    total && withOrders !== undefined && total > 0
      ? `${((withOrders / total) * 100).toFixed(1)}%`
      : undefined;

  const refreshing = lifetime.isFetching || today.isFetching;

  return (
    <section className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          {t('customers.overview')}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void lifetime.refetch();
            void today.refetch();
          }}
          disabled={refreshing}
          className="text-muted-foreground h-7 gap-1.5 text-xs"
        >
          <RefreshCw className={cn('size-3.5', refreshing && 'animate-spin')} />
          <span className="hidden sm:inline">{t('common.reload')}</span>
        </Button>
      </div>

      {/* Three cards, so three columns — the original declared `sm:grid-cols-4`
          and left a gap where a fourth would have gone. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard
          icon={Users}
          label={t('customers.registered')}
          value={total}
          loading={lifetime.isPending}
          busy={lifetime.isFetching}
          failed={lifetime.isError}
        />
        <StatCard
          icon={UserPlus}
          label={t('customers.registeredToday')}
          value={today.data?.totalUsers}
          loading={today.isPending}
          busy={today.isFetching}
          failed={today.isError}
        />
        <StatCard
          icon={ShoppingBag}
          label={t('customers.atLeastOneOrder')}
          value={withOrders}
          hint={share}
          loading={lifetime.isPending}
          busy={lifetime.isFetching}
          failed={lifetime.isError}
        />
      </div>
    </section>
  );
}
